import { TagIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled, { css } from "styled-components";
import AutoComplete from "../AutoComplete/AutoComplete";
import Toast from "../Toast";
import Tag from "./Tag";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import Modal from "../Modal/Modal";
import Input from "../Input/Input";
import BottomSheet from "../BottomSheet/BottomSheet";

interface TagEditFormProps {
  placeholder?: string;
  onDuplicate?: () => void;
  tags: string[];
  setTags: React.Dispatch<React.SetStateAction<string[]>>;
  autocomplete?: {
    label: string;
    value: string;
  }[];

  // 태그 변경 이벤트 콜백
  onAddTag?: (tag: string) => void;
  onDeleteTag?: (tag: string) => void;
  onRenameTag?: (oldTag: string, newTag: string) => void;
}

export interface TagFormData {
  tag: string;
}

const TagEditForm = ({
  placeholder,
  // onDuplicate,
  tags,
  setTags,
  autocomplete,
  onAddTag,
  onDeleteTag,
  onRenameTag,
}: TagEditFormProps) => {
  const { control, handleSubmit, reset, setValue } =
    useFormContext<TagFormData>();
  const [isTagLengthErrorToast, setIsTagLengthErrorToast] = useState(false);
  const [isTagEmptyErrorToast, setIsTagEmptyErrorToast] = useState(false);
  const [isTagNotChangeErrorToast, setIsTagNotChangeErrorToast] =
    useState(false);
  const [editTargetTag, setEditTargetTag] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditBottomSheetOpen, setIsEditBottomSheetOpen] = useState(false);
  const [editInputValue, setEditInputValue] = useState("");
  const [isDuplicateTag, setIsDuplicateTag] = useState(false);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const inputRef = useRef<HTMLInputElement>(null);

  const tagSubmit = (data: TagFormData) => {
    const valid = validateTag(data.tag);
    if (!valid) return;

    setTags((prev) => [...prev, valid]);

    onAddTag?.(valid);

    reset();
  };

  const validateTag = (value: string, options?: { oldTag?: string }) => {
    const trimmed = value.trim();
    const oldTag = options?.oldTag;

    if (trimmed === "") {
      setIsTagEmptyErrorToast(true);
      return null;
    }

    if (trimmed.length > 20) {
      setIsTagLengthErrorToast(true);
      return null;
    }

    // oldTag와 완전히 같으면 변경 없음
    if (oldTag && trimmed === oldTag) {
      setIsTagNotChangeErrorToast(true);
      return null;
    }

    // 중복 체크 (수정인 경우 oldTag는 예외)
    if (tags.includes(trimmed) && trimmed !== oldTag) {
      // onDuplicate && onDuplicate();
      setIsEditModalOpen(false);
      setIsDuplicateTag(true);
      return null;
    }

    return trimmed;
  };

  const bottomSheetList = [
    {
      label: "수정",
      value: "edit",
    },
    {
      label: "삭제",
      value: "delete",
    },
  ];

  // 삭제
  const handleDeleteTag = (tag: string) => {
    setTags((prev) => prev.filter((prevTag) => prevTag !== tag));

    onDeleteTag?.(tag);
  };

  const handleEditTag = (oldTag: string, newTag: string) => {
    const updated = newTag.trim();

    if (oldTag === updated) return;

    setTags((prev) => {
      const filtered = prev.filter((tag) => tag !== updated);
      return filtered.map((tag) => (tag === oldTag ? updated : tag));
    });

    onRenameTag?.(oldTag, updated);
  };

  const handleConfirmEdit = () => {
    if (!editTargetTag) return;

    const valid = validateTag(editInputValue, { oldTag: editTargetTag });
    if (!valid) return;

    handleEditTag(editTargetTag, valid);
    // 모달/상태 정리
    setEditTargetTag(null);
    setEditInputValue("");
    setIsEditModalOpen(false); // 쓰고 있다면
  };

  useEffect(() => {
    if (editTargetTag) setEditInputValue(editTargetTag);
  }, [editTargetTag]);

  // 수정 버튼 눌렀을 때: 태그만 기억하고 모달만 열기
  const handleClickEditButton = (tag: string) => {
    if (!isMobile) {
      setEditTargetTag(tag);
      setIsEditModalOpen(true);
    } else {
      setEditTargetTag(tag);
      setIsEditBottomSheetOpen(true);
    }
  };

  // 모달에서 "취소"
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditTargetTag(null);
  };

  // 모달에서 "삭제 확인"
  const handleConfirmDelete = () => {
    if (editTargetTag) {
      handleDeleteTag(editTargetTag); // 여기서 기존 삭제 함수 호출
    }
    setIsEditModalOpen(false);
    setEditTargetTag(null);
  };

  const sensors = useSensor(PointerSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setTags((items) => {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <>
      <Toast.Message
        open={isTagLengthErrorToast}
        onOpenChange={setIsTagLengthErrorToast}
      >
        태그는 최대 20자까지 입력이 가능합니다.
      </Toast.Message>
      <Toast.Message
        open={isTagEmptyErrorToast}
        onOpenChange={setIsTagEmptyErrorToast}
      >
        내용이 입력되지 않았습니다.
      </Toast.Message>
      <Toast.Message
        open={isTagNotChangeErrorToast}
        onOpenChange={setIsTagNotChangeErrorToast}
      >
        수정사항이 없습니다.
      </Toast.Message>
      <TagEditFormLayout>
        <form onSubmit={handleSubmit(tagSubmit)}>
          <TagInputLayout>
            <TagIcon />
            <AutoComplete
              inputRef={inputRef}
              autocomplete={autocomplete}
              onSelect={(value) => {
                setValue("tag", value);
              }}
            >
              {(ref) => (
                <div>
                  <Controller
                    control={control}
                    name="tag"
                    render={({ field: { onChange, value } }) => {
                      return (
                        <input
                          autoComplete="off"
                          placeholder={placeholder}
                          value={value}
                          ref={inputRef}
                          onChange={(e) => {
                            if (e.target.value.length > 20) {
                              setIsTagLengthErrorToast(true);
                            }
                            onChange(e);
                          }}
                          onKeyUp={(e) => {
                            if (e.key === "ArrowDown") {
                              ref.current?.[0]?.focus();
                            }
                          }}
                        />
                      );
                    }}
                  />
                </div>
              )}
            </AutoComplete>
            <Button
              variant="tertiary"
              type="submit"
              style={{ whiteSpace: "nowrap" }}
            >
              추가
            </Button>
          </TagInputLayout>
        </form>
        <Divider />

        <TagList>
          <DndContext
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
            sensors={[sensors]}
          >
            <SortableContext
              items={tags}
              strategy={horizontalListSortingStrategy}
            >
              {tags.map((tag) => (
                <Tag
                  key={tag}
                  id={tag}
                  variant="grey"
                  style={{ cursor: "grab" }}
                  // onClickRemove={() => handleDeleteTag(tag)}
                  onClickEdit={() => handleClickEditButton(tag)}
                >
                  <p>{tag}</p>
                </Tag>
              ))}
            </SortableContext>
          </DndContext>
        </TagList>
      </TagEditFormLayout>
      {/* 삭제 확인 모달 */}
      {!isMobile && (
        <Modal.Root
          open={isEditModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              // ESC / 바깥 클릭 등으로 닫힐 때도 state 정리
              setIsEditModalOpen(false);
              setEditTargetTag(null);
            } else {
              setIsEditModalOpen(true);
            }
          }}
        >
          <Modal.Body
            title="태그 수정 / 삭제"
            onCancel={handleCancelEdit}
            cancelValue="취소"
            onConfirm={handleConfirmDelete}
            confirmValue="삭제"
            confirm
            confirmType={"danger"}
          >
            {editTargetTag && (
              <>
                {/*`{editTargetTag}` 태그를 삭제하시겠습니까?*/}
                {/*<br />*/}
                {/*삭제 후에는 되돌릴 수 없습니다.*/}
                <Input
                  value={editInputValue}
                  onChange={(e) => setEditInputValue(e.target.value)}
                  style={{ display: "inline" }}
                ></Input>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleConfirmEdit}
                  style={{ display: "inline" }}
                >
                  {"수정"}
                </Button>
              </>
            )}
          </Modal.Body>
        </Modal.Root>
      )}
      {isMobile && (
        <BottomSheet
          isOpen={isEditBottomSheetOpen}
          setOpen={setIsEditBottomSheetOpen}
          title={"TAG"}
        >
          {bottomSheetList.map((list) => (
            <div
              key={list?.value}
              onClick={(e) => {
                e.stopPropagation();
                if (list.value === "edit") {
                  setIsEditModalOpen(true);
                } else {
                  handleConfirmDelete();
                }
                setIsEditBottomSheetOpen(false);
              }}
            >
              {list?.label}
            </div>
          ))}
        </BottomSheet>
      )}
      {isMobile && (
        <Modal.Root
          open={isEditModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              // ESC / 바깥 클릭 등으로 닫힐 때도 state 정리
              // setIsEditModalOpen(false);
              // setEditTargetTag(null);
            } else {
              setIsEditModalOpen(true);
            }
          }}
        >
          <Modal.Body
            title="태그 수정"
            onCancel={handleCancelEdit}
            cancelValue="취소"
            onConfirm={handleConfirmEdit}
            confirmValue="수정"
            confirm
          >
            {editTargetTag && (
              <>
                {/*`{editTargetTag}` 태그를 삭제하시겠습니까?*/}
                {/*<br />*/}
                {/*삭제 후에는 되돌릴 수 없습니다.*/}
                <Input
                  value={editInputValue}
                  onChange={(e) => {
                    setEditInputValue(e.target.value);
                  }}
                  style={{ display: "inline" }}
                ></Input>
              </>
            )}
          </Modal.Body>
        </Modal.Root>
      )}
      <Modal.Root open={isDuplicateTag} onOpenChange={setIsDuplicateTag}>
        <Modal.Body
          title="이미 존재하는 태그입니다."
          onConfirm={() => {
            setIsDuplicateTag(false);
            setIsEditModalOpen(true);
          }}
        />
      </Modal.Root>
    </>
  );
};

export default TagEditForm;

const TagEditFormLayout = styled.div`
  padding: 2rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  border-radius: 1rem;
  background-color: ${({ theme }) => theme.color.grey[200]};

  &:hover {
    background-color: #eceef4;
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      padding: 1.6rem;
    }
  `}
`;

const TagInputLayout = styled.div`
  display: flex;
  align-items: center;
  svg {
    color: ${({ theme }) => theme.color.icon.tertiary};
  }
  > div {
    margin: 0rem 1.2rem 0rem 0.8rem;
    background-color: transparent;
    flex-grow: 1;
  }

  input {
    ${({ theme }) => theme.typography.body1.regular};
    border: none;
    background-color: transparent;
    width: 100%;
    flex-grow: 1;
  }
  button {
    background-color: ${({ theme }) => theme.color.white};
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      input {
        ${({ theme }) => theme.typography.body3.regular};
      }

      button {
        ${({ theme }) => theme.typography.body3.medium};
      }

      svg {
        width: 1.6rem;
        height: 1.6rem;
      }
    }
  `}
`;

const TagList = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;

  padding: 1rem 3.2rem;

  svg {
    cursor: pointer;
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      ${theme.typography.caption1.regular};
      padding: 0rem 2.4rem;
    }
  `}
`;
